import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomModal from "../../../../components/Modals/CustomModal";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import IconComponent from "../../../../components/Icon/IconComponent";
import Image from "../../../../components/Image/Image";
import SkeletonLoader from "../../../../components/Loaders/SkeletonLoader";
import { Button } from "../../../../components/Buttons";
import { styles } from "./styles";
import { STRINGS } from "../../strings";
import { postRequest } from "../../../../Apis";
import { campaignEndpoints } from "../../../../utils/constants/httpConstants";
import { ESP_IMAGES_BY_INDEX } from "../../../../utils/constants";
import {
  GREY,
  PRIMARY,
  SUCCESS,
  WARNING,
  ERROR,
} from "../../../../utils/constants/colors";
import { ICON_LABELS, fontSize, fontWeight } from "../../../../utils/constants/UI";

// Pick a fill color for the usage bar based on how much of the bucket is used up.
const usageColor = (sent, limit) => {
  if (!limit || limit <= 0) return PRIMARY.W_500;
  const ratio = sent / limit;
  if (ratio >= 1) return ERROR.W_500;
  if (ratio >= 0.8) return WARNING.W_500;
  return SUCCESS.W_500;
};

const MailboxUsageModal = ({ open, setOpen, mailbox, prefetched }) => {
  const classes = styles();
  const navigate = useNavigate();

  // If the tab already bulk-fetched this mailbox's status, seed with it and skip
  // the redundant per-mailbox call; otherwise fetch on open.
  const [isLoading, setIsLoading] = useState(!prefetched);
  const [isError, setIsError] = useState(false);
  const [status, setStatus] = useState(prefetched || null);

  // Gmail/Outlook rows carry `email`; "other ESP" rows surface their identity in
  // `key`. The bucket lookup is keyed by the actual sending address.
  const email = mailbox?.email || mailbox?.key || "";
  const logoSrc = ESP_IMAGES_BY_INDEX?.[parseInt(mailbox?.esp)];

  useEffect(() => {
    let active = true;
    // Prefetched from the tab-level bulk call — no need to hit the API again.
    if (prefetched) {
      setStatus(prefetched);
      setIsLoading(false);
      setIsError(false);
      return;
    }
    const fetchUsage = async () => {
      if (!email) return;
      try {
        setIsLoading(true);
        setIsError(false);
        const response = await postRequest(
          navigate,
          campaignEndpoints.mailboxSendingStatus,
          { emails: [email] },
        );
        if (!active) return;
        // API shape: { data: { mailboxes: [ { email, found, hourly, daily, ... } ] } }
        // Fall back to a few other shapes defensively.
        const payload = response?.data?.data || response?.data || {};
        const arr = Array.isArray(payload)
          ? payload
          : payload?.mailboxes || payload?.results || [];
        const match =
          arr.find((m) => m?.email === email) || arr?.[0] || null;
        setStatus(match);
      } catch (e) {
        if (active) setIsError(true);
      } finally {
        if (active) setIsLoading(false);
      }
    };
    fetchUsage();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, prefetched]);

  const renderUsageCard = (iconLabel, title, bucket) => {
    const sent = bucket?.sent ?? 0;
    const limit = bucket?.limit ?? 0;
    const remaining = bucket?.remaining ?? Math.max(0, limit - sent);
    const pct = limit > 0 ? Math.min(100, Math.round((sent / limit) * 100)) : 0;
    return (
      <div className={classes.usageCard}>
        <div className={classes.usageCardHead}>
          <span className={classes.usageCardIcon}>
            <IconComponent
              iconLabel={iconLabel}
              color={PRIMARY.W_500}
              fontSize={fontSize.SM}
            />
          </span>
          <TypographyInline
            size={"sm"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.SEMIBOLD}
            label={title}
          />
        </div>
        <div className={classes.usageProgressTrack}>
          <div
            className={classes.usageProgressFill}
            style={{
              width: `${pct}%`,
              background: usageColor(sent, limit),
            }}
          />
        </div>
        <div className={classes.usageStatsRow}>
          <div className={classes.usageStat}>
            <span className={classes.usageStatLabel}>{STRINGS.SENT}</span>
            <span className={classes.usageStatValue}>{sent}</span>
          </div>
          <div className={classes.usageStatDivider} />
          <div className={classes.usageStat}>
            <span className={classes.usageStatLabel}>{STRINGS.REMAINING}</span>
            <span
              className={classes.usageStatValue}
              style={{ color: usageColor(sent, limit) }}
            >
              {remaining}
            </span>
          </div>
          <div className={classes.usageStatDivider} />
          <div className={classes.usageStat}>
            <span className={classes.usageStatLabel}>{STRINGS.LIMIT}</span>
            <span className={classes.usageStatValue}>{limit}</span>
          </div>
        </div>
      </div>
    );
  };

  // The BE IST string looks like "6/29/2026, 8:33:22 PM". In a reset/next-send
  // context the *time* is what matters, so pull the time portion out (dropping
  // the seconds) and keep the date only as secondary context.
  const splitIstTime = (raw) => {
    if (!raw) return "";
    const parts = String(raw).split(",");
    const rest = (parts.length > 1 ? parts.slice(1).join(",") : raw).trim();
    // "8:33:22 PM" -> "8:33 PM"
    const time = rest.replace(/(\d{1,2}:\d{2}):\d{2}(\s*[AP]M)?/i, "$1$2");
    return time || rest;
  };

  // Each reset renders as a self-contained tile: label on top, time below, and
  // the countdown/note as a chip underneath — nothing competes for horizontal
  // space, so long timestamps can never overlap the label.
  const renderMetaTile = (iconLabel, label, reset) => {
    if (!reset) return null;
    const time = splitIstTime(reset?.ist || reset?.utc);
    const mins = reset?.in_minutes;
    const note = reset?.note;
    const chip =
      Number.isFinite(mins) && mins > 0
        ? `in ${mins} ${STRINGS.IN_MINUTES_SUFFIX}`
        : "";
    // The window can be "open" — the BE sends only a note and no timestamp.
    // Show the note as the tile's main content in that case.
    const hasTime = !!time;
    return (
      <div className={classes.usageMetaTile}>
        <div className={classes.usageMetaTileHead}>
          <IconComponent
            iconLabel={iconLabel}
            color={GREY.TERTIARY}
            fontSize={fontSize.XS}
          />
          <span className={classes.usageMetaTileLabel}>{label}</span>
        </div>
        {hasTime ? (
          <>
            <span
              className={classes.usageMetaTileTime}
              title={reset?.ist || ""}
            >
              {time}
            </span>
            {chip && <span className={classes.usageMetaTileChip}>{chip}</span>}
          </>
        ) : (
          <span className={classes.usageMetaTileNote} title={note || ""}>
            {note || "—"}
          </span>
        )}
      </div>
    );
  };

  // Prominent status banner for "next send allowed": green when the mailbox can
  // send right now, amber with a countdown when it has to wait.
  const renderNextSendBanner = (reset) => {
    const mins = reset?.in_minutes;
    const canSendNow =
      !!reset?.note || !Number.isFinite(mins) || mins <= 0;
    const time = splitIstTime(reset?.ist || reset?.utc);
    const detail = canSendNow
      ? reset?.note || "Ready to send"
      : `Next send at ${time} · in ${mins} ${STRINGS.IN_MINUTES_SUFFIX}`;
    return (
      <div
        className={classes.usageNextSend}
        style={{
          background: canSendNow ? SUCCESS.W_50 : WARNING.W_50,
          borderColor: canSendNow ? SUCCESS.W_100 : WARNING.W_100,
        }}
      >
        <span
          className={classes.usageNextSendIcon}
          style={{
            background: canSendNow ? SUCCESS.W_100 : WARNING.W_100,
            color: canSendNow ? SUCCESS.W_600 : WARNING.W_600,
          }}
        >
          <IconComponent
            iconLabel={canSendNow ? ICON_LABELS.CHECK : ICON_LABELS.TIMER}
            fontSize={fontSize.SM}
            color={canSendNow ? SUCCESS.W_600 : WARNING.W_600}
          />
        </span>
        <div className={classes.usageNextSendText}>
          <span className={classes.usageNextSendLabel}>
            {STRINGS.NEXT_SEND_ALLOWED}
          </span>
          <span
            className={classes.usageNextSendDetail}
            style={{ color: canSendNow ? SUCCESS.W_700 : WARNING.W_700 }}
          >
            {detail}
          </span>
        </div>
      </div>
    );
  };

  const modalHeaderData = {
    heading: {
      id: "usage",
      label: STRINGS.SENDING_USAGE,
    },
    description: {
      id: "usage_desc",
      label: STRINGS.MAILBOX_USAGE_DESC,
    },
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className={classes.usageSkeleton}>
          <SkeletonLoader type="rounded" skeletonWidth={"100%"} skeletonHeight={28} />
          <div className={classes.usageCardsGrid}>
            <SkeletonLoader type="rounded" skeletonWidth={"100%"} skeletonHeight={130} />
            <SkeletonLoader type="rounded" skeletonWidth={"100%"} skeletonHeight={130} />
          </div>
          <SkeletonLoader type="rounded" skeletonWidth={"100%"} skeletonHeight={110} />
        </div>
      );
    }

    if (isError) {
      return (
        <div className={classes.usageEmpty}>
          <span className={classes.usageEmptyIcon}>
            <IconComponent
              iconLabel={ICON_LABELS.INFO}
              color={ERROR.W_500}
              fontSize={"24px"}
            />
          </span>
          <TypographyInline
            size={"md"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.SEMIBOLD}
            label={STRINGS.USAGE_FETCH_ERROR}
          />
        </div>
      );
    }

    const found = status?.found;

    return (
      <div className={classes.usageModalBody}>
        {/* Mailbox identity */}
        <div className={classes.usageMailboxHeader}>
          {logoSrc && (
            <Image
              src={logoSrc}
              alt={"mailbox"}
              className={classes.usageMailboxLogo}
            />
          )}
          <span className={classes.usageMailboxEmail} title={email}>
            {email}
          </span>
        </div>

        {found ? (
          <>
            <div className={classes.usageCardsGrid}>
              {renderUsageCard(
                ICON_LABELS.ACCESS_TIME,
                STRINGS.HOURLY_USAGE,
                status?.hourly,
              )}
              {renderUsageCard(
                ICON_LABELS.INSIGHTS,
                STRINGS.DAILY_USAGE,
                status?.daily,
              )}
            </div>

            {/* Next send allowed — the headline signal, so give it a banner. */}
            {status?.next_send_allowed_at &&
              renderNextSendBanner(status?.next_send_allowed_at)}

            {/* Reset windows sit below as an even two-tile grid. */}
            <div className={classes.usageMetaGrid}>
              {renderMetaTile(
                ICON_LABELS.TIMER,
                STRINGS.NEXT_HOURLY_RESET,
                status?.next_hourly_reset,
              )}
              {renderMetaTile(
                ICON_LABELS.TIMER,
                STRINGS.NEXT_DAILY_RESET,
                status?.next_daily_reset,
              )}
            </div>
          </>
        ) : (
          <div className={classes.usageEmpty}>
            <span className={classes.usageEmptyIcon}>
              <IconComponent
                iconLabel={ICON_LABELS.INSIGHTS}
                color={GREY.TERTIARY}
                fontSize={"24px"}
              />
            </span>
            <TypographyInline
              size={"md"}
              color={GREY.PRIMARY}
              fontWeight={fontWeight.SEMIBOLD}
              label={STRINGS.USAGE_NO_SENDS_TITLE}
            />
            <TypographyInline
              size={"sm"}
              color={GREY.SECONDARY}
              fontWeight={fontWeight.REGULAR}
              label={STRINGS.USAGE_NO_SENDS_DESC}
            />
          </div>
        )}
      </div>
    );
  };

  const modalFooterRightData = (
    <Button
      label={STRINGS.CLOSE}
      variant={"ghost"}
      size={"sm36"}
      onClick={() => setOpen(false)}
    />
  );

  return (
    <CustomModal
      open={open}
      width={"520px"}
      close={() => setOpen(false)}
      onClose={() => setOpen(false)}
      header={modalHeaderData}
      children={renderBody()}
      footer={{ right: modalFooterRightData }}
    />
  );
};

export default MailboxUsageModal;
